import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, CATEGORIES, PERIODS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Download } from "lucide-react";

const STATUSES = ["nuovo", "in_lavorazione", "chiuso", "annullato"];
const STATUS_COLORS = {
  "nuovo": "bg-blue-100 text-blue-800",
  "in_lavorazione": "bg-amber-100 text-amber-800",
  "chiuso": "bg-emerald-100 text-emerald-800",
  "annullato": "bg-slate-200 text-slate-700",
};

export default function AdminQuotes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ category: "all", city: "all", status: "all", period: "all" });
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!loading && (!user || (user.role !== "personale" && user.role !== "admin"))) navigate("/login");
  }, [user, loading, navigate]);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams(f).toString();
      const r = await api.get(`/admin/quotes?${params}`);
      setQuotes(r.data);
      const uniqCities = [...new Set(r.data.map(q => q.city))].filter(Boolean);
      setCities(uniqCities);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (user?.role === "personale" || user?.role === "admin") load(); /* eslint-disable-next-line */ }, [f, user]);

  const exportCsv = async () => {
    const params = new URLSearchParams(f).toString();
    const r = await api.get(`/admin/quotes/export?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `preventivi_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading || !(user?.role === "personale" || user?.role === "admin")) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900">Preventivi</h1>
            <p className="text-slate-600">Gestisci tutte le richieste</p>
          </div>
          <Button onClick={exportCsv} variant="outline" data-testid="admin-export-csv-btn">
            <Download className="w-4 h-4 mr-2"/> Esporta CSV
          </Button>
        </div>

        <Card className="p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <FilterSelect label="Categoria" value={f.category} onChange={v => setF({...f, category: v})}
                        options={[{value:"all",label:"Tutte"}, ...CATEGORIES.map(c=>({value:c,label:c}))]}
                        testid="filter-category"/>
          <FilterSelect label="Città" value={f.city} onChange={v => setF({...f, city: v})}
                        options={[{value:"all",label:"Tutte"}, ...cities.filter(c => c && c.trim()).map(c=>({value:c,label:c}))]}
                        testid="filter-city"/>
          <FilterSelect label="Stato" value={f.status} onChange={v => setF({...f, status: v})}
                        options={[{value:"all",label:"Tutti"}, ...STATUSES.map(s=>({value:s,label:s}))]}
                        testid="filter-status"/>
          <FilterSelect label="Periodo" value={f.period} onChange={v => setF({...f, period: v})}
                        options={PERIODS} testid="filter-period"/>
        </Card>

        {busy ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div> : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-quotes-table">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left p-3">ID</th>
                  <th className="text-left p-3">Data</th>
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Categoria</th>
                  <th className="text-left p-3">Città</th>
                  <th className="text-left p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">Nessun preventivo</td></tr>}
                {quotes.map(q => (
                  <tr key={q.quote_id} className="border-t hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/preventivi/${q.quote_id}`)}
                      data-testid={`admin-quote-row-${q.quote_id}`}>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">
                      {q.unread_by_staff && <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse" data-testid={`unread-dot-${q.quote_id}`}/>}
                      {q.quote_id}
                    </td>
                    <td className="p-3 whitespace-nowrap">{new Date(q.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-3">{q.name}<div className="text-xs text-slate-500">{q.email || q.phone}</div></td>
                    <td className="p-3">{q.category}</td>
                    <td className="p-3">{q.city}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || "bg-slate-100"}`}>{q.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue/></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
